import React from 'react';
import { useCRM } from '../context/CRMContext';
import { 
  TrendingUp, 
  Users, 
  UserPlus, 
  Phone, 
  ArrowUpRight, 
  Calendar,
  Target,
  Briefcase
} from 'lucide-react';

const Dashboard = () => {
  const { investors, salesLeads, candidates, contacts } = useCRM();

  const stats = [
    {
      name: 'Active Investors',
      value: investors.filter(i => i.status === 'interested' || i.status === 'meeting_scheduled').length,
      total: investors.length,
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      name: 'Hot Sales Leads',
      value: salesLeads.filter(l => l.priority === 'high' || l.status === 'qualified').length,
      total: salesLeads.length,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      name: 'Top Candidates',
      value: candidates.filter(c => c.status === 'interviewing' || c.status === 'offer_extended').length,
      total: candidates.length,
      icon: UserPlus,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      name: 'Total Contacts',
      value: contacts.length,
      total: contacts.length,
      icon: Phone,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50'
    }
  ];

  const recentActivities = [
    {
      type: 'investor',
      message: 'New investor added to pipeline',
      time: '2 hours ago',
      icon: TrendingUp,
      color: 'text-green-600'
    },
    {
      type: 'sales',
      message: 'Sales call scheduled with TechCorp',
      time: '4 hours ago',
      icon: Calendar,
      color: 'text-blue-600'
    },
    {
      type: 'recruitment',
      message: 'Developer interview completed',
      time: '1 day ago',
      icon: UserPlus,
      color: 'text-purple-600'
    }
  ];

  const quickActions = [
    {
      name: 'Add Investor',
      description: 'Connect with potential investors',
      href: '/investors',
      icon: TrendingUp,
      color: 'bg-green-500'
    },
    {
      name: 'New Sales Lead',
      description: 'Track potential customers',
      href: '/sales-leads',
      icon: Target,
      color: 'bg-blue-500'
    },
    {
      name: 'Add Candidate',
      description: 'Find your next hire',
      href: '/recruitment',
      icon: Briefcase,
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome back! Here's what's happening with your startup.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <div className="flex items-center mt-1">
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  {stat.total > stat.value && (
                    <span className="text-sm text-gray-500 ml-2">/ {stat.total}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {quickActions.map((action, index) => (
                <a
                  key={index}
                  href={action.href}
                  className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-md transition-all group"
                >
                  <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center mb-3`}>
                    <action.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-medium text-gray-900 group-hover:text-primary-700">
                    {action.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                  <ArrowUpRight className="h-4 w-4 text-gray-400 mt-2 group-hover:text-primary-500" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-4">
            {recentActivities.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className={`p-1 rounded-full bg-gray-100`}>
                  <activity.icon className={`h-4 w-4 ${activity.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Startup Journey Progress */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Startup Journey</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium text-gray-900">Idea Validation</h3>
              <p className="text-sm text-gray-600">Define your MVP and validate with potential customers</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-sm text-green-600 font-medium">Complete</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
            <div>
              <h3 className="font-medium text-gray-900">Team Building & Fundraising</h3>
              <p className="text-sm text-gray-600">Build your core team and secure initial funding</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-blue-600 font-medium">In Progress</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg opacity-60">
            <div>
              <h3 className="font-medium text-gray-900">Product Development</h3>
              <p className="text-sm text-gray-600">Build and iterate on your minimum viable product</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <span className="text-sm text-gray-500 font-medium">Upcoming</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;